var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import _ from 'lodash';
import autobind from 'autobind-decorator';
import { getSnapshot } from 'mobx-state-tree';
import GraphQlClient from './GraphQlClient';
import { cleanupTaggedTemplateString, isEmptyOrWhiteSpace } from '../../../helpers/StringHelpers';
import { generateGuid } from '../../../helpers/RandomGenerator';
var prototypeOfDate = Object.getPrototypeOf(new Date());
var prototypeOfArray = Object.getPrototypeOf([]);
function stringify(obj) {
    if (Array.isArray(obj)) {
        return "[" + obj
            .map(function (x) { return stringify(x); })
            .join(',') + "]";
    }
    if (typeof obj !== 'object') {
        // not an object, stringify using native function
        return JSON.stringify(obj);
    }
    // if obj is Date => use JSON.stringify as well
    if (Object.getPrototypeOf(obj) === prototypeOfDate) {
        return JSON.stringify(obj);
    }
    // Implements recursive object serialization according to JSON spec
    // but without quotes around the keys.
    var props = Object
        .keys(obj)
        // ignore undefined values and empty arrays
        .filter(function (key) { return obj[key] !== undefined
        && (Object.getPrototypeOf(obj[key]) !== prototypeOfArray || obj[key].length !== 0); })
        // ignore private values
        .filter(function (key) { return !key.startsWith('_'); })
        .map(function (key) { return key + ":" + stringify(obj[key]); })
        .join(',');
    return "{" + props + "}";
}
var GraphQlService = /** @class */ (function () {
    function GraphQlService(apiWrapper, apiName, detailDescription, queryDescription, mutationKey) {
        this.apiName = apiName;
        this.detailDescription = detailDescription;
        this.queryDescription = queryDescription;
        this.graphQlClient = new GraphQlClient(apiWrapper, this.apiName);
        this.mutationKey = _.lowerFirst(mutationKey);
    }
    GraphQlService.prototype.get = function (id) {
        return this.querySingle(this.detailDescription, id);
    };
    GraphQlService.prototype.query = function (queryParameters, customQueryDescription) {
        return this.queryMany(customQueryDescription || this.queryDescription, queryParameters);
    };
    GraphQlService.prototype.create = function (entity, metadata) {
        var mutationString = "create" + _.upperFirst(this.mutationKey) + "(" + this.mutationKey + ":" + stringify(entity) + ")" + (isEmptyOrWhiteSpace(metadata) ? '' : metadata);
        return this.graphQlClient.executeMutation(mutationString);
    };
    GraphQlService.prototype.patch = function (id, jsonPatchDocument) {
        console.log(jsonPatchDocument);
        var mutationString = "patch" + _.upperFirst(this.mutationKey) + "(id: \"" + id + "\", " + this.mutationKey + "Patch:" + stringify(jsonPatchDocument) + ")";
        return this.graphQlClient.executeMutation(mutationString);
    };
    GraphQlService.prototype.delete = function (id) {
        var mutationString = "delete" + _.upperFirst(this.mutationKey) + "(id: \"" + id + "\")";
        return this.graphQlClient.executeMutation(mutationString);
    };
    GraphQlService.prototype.querySingle = function (queryDescription, id) {
        return __awaiter(this, void 0, void 0, function () {
            var splittedQueryDescription, fullQuery, apiResult;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // cleanup tagged template string to avoid errors
                        queryDescription = cleanupTaggedTemplateString(queryDescription);
                        splittedQueryDescription = queryDescription.split(/{(.+)/);
                        fullQuery = "{" + splittedQueryDescription[0] + "(id: \"" + id + "\"){" + splittedQueryDescription[1] + "}";
                        console.log('Sending GraphQL GET', fullQuery);
                        return [4 /*yield*/, this.graphQlClient.querySingle(fullQuery)];
                    case 1:
                        apiResult = _a.sent();
                        console.log('received GraphQL GET response', apiResult);
                        return [2 /*return*/, apiResult];
                }
            });
        });
    };
    GraphQlService.prototype.queryMany = function (queryDescription, queryParameters) {
        return __awaiter(this, void 0, void 0, function () {
            var splittedQueryDescription, jsonQueryParameters, fullQuery, queryId, apiResult;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // cleanup tagged template string to avoid errors
                        queryDescription = cleanupTaggedTemplateString(queryDescription);
                        splittedQueryDescription = queryDescription.split(/{(.+)/);
                        jsonQueryParameters = stringify(getSnapshot(queryParameters));
                        fullQuery = "{" + splittedQueryDescription[0] + "(query: " + jsonQueryParameters + "){" + splittedQueryDescription[1] + "}";
                        queryId = generateGuid();
                        console.log("Sending GraphQL Query " + queryId, fullQuery);
                        return [4 /*yield*/, this.graphQlClient.querySingle(fullQuery)];
                    case 1:
                        apiResult = _a.sent();
                        console.log("received GraphQL Query response  " + queryId, apiResult);
                        return [2 /*return*/, apiResult];
                }
            });
        });
    };
    __decorate([
        autobind
    ], GraphQlService.prototype, "get", null);
    __decorate([
        autobind
    ], GraphQlService.prototype, "query", null);
    __decorate([
        autobind
    ], GraphQlService.prototype, "create", null);
    __decorate([
        autobind
    ], GraphQlService.prototype, "patch", null);
    __decorate([
        autobind
    ], GraphQlService.prototype, "delete", null);
    return GraphQlService;
}());
export default GraphQlService;
//# sourceMappingURL=GraphQlService.js.map